import React, { useCallback, useState, useMemo } from 'react';
import { GeneralTabProps } from '../utils/corpClientTypes';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Table,
  Typography,
  notification,
  theme,
  Popconfirm,
} from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useMutation, useQuery } from '@apollo/client';
import {
  QUERY_LIST_ASSIGNMENT_EXTENSIONS,
  MUTATION_CREATE_ASSIGNMENT_EXTENSION,
} from '../../../../queries/CorporateClientsQueries';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import numeral from 'numeral';
import { CURRENCY_FORMAT } from 'constants/numberformats';

dayjs.extend(localizedFormat);

const { TextArea } = Input;

const ExtensionTab: React.FC<GeneralTabProps> = ({ user, corpClientData }) => {
  const { dbUser } = user;
  const corpClient = corpClientData?.getCorporateClient;
  const [form] = Form.useForm();
  const days = Form.useWatch('days', form);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = theme.useToken();

  const {
    data: extensionsData,
    loading: extensionsLoading,
    refetch: refetchExtensions,
  } = useQuery(QUERY_LIST_ASSIGNMENT_EXTENSIONS, {
    variables: {
      where: {
        corporate_client_id: { eq: corpClient?.corporate_client_id },
      },
      order: [{ field: 'started_on', order: 'DESC' }],
    },
  });

  const now = dayjs();
  const activeExtension = useMemo(() => {
    const list = extensionsData?.listAssignmentExtension?.list || [];
    return list.find(
      (ext: any) => ext.end_date && dayjs(ext.end_date).isAfter(now),
    );
  }, [extensionsData, now]);

  const [createExtension] = useMutation(MUTATION_CREATE_ASSIGNMENT_EXTENSION, {
    onCompleted: () => {
      refetchExtensions();
      form.resetFields();
      notification.success({
        message: 'Success',
        description: 'Assignment extension created successfully',
      });
    },
    onError: (error) => {
      notification.error({
        message: 'Error',
        description: error.message,
      });
    },
  });

  const handleEditSubmit = useCallback(
    async (values: any) => {
      setIsLoading(true);
      try {
        await createExtension({
          variables: {
            input: {
              corporate_client_id: corpClient?.corporate_client_id,
              days: values.days,
              started_by: dbUser?.user_id,
              started_on: new Date().toISOString(),
              internal_note: values.internal_note,
              external_message: values.external_message,
            },
          },
        });
      } finally {
        setIsLoading(false);
      }
    },
    [corpClient?.corporate_client_id, createExtension, dbUser?.user_id],
  );

  const helperText = useMemo(() => {
    if (typeof days !== 'number') return '';
    const futureDate = dayjs().add(days, 'days').format('MMMM D, YYYY');
    return (
      <div>
        Assignments with dates in the past will be set to today +{' '}
        <span style={{ color: token.colorSuccess }}>{days}</span> day(s):{' '}
        <span style={{ color: token.colorSuccess }}>{futureDate}</span>.
      </div>
    );
  }, [days, token.colorSuccess]);

  const columns: ColumnType<any>[] = [
    {
      title: 'Started On',
      dataIndex: 'started_on',
      key: 'started_on',
      render: (date: string) => dayjs(date).format('LLL'),
      width: 180,
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
      width: 100,
    },
    {
      title: 'Started By',
      dataIndex: ['started_by_user', 'full_name'],
      key: 'created_by',
      width: 150,
    },
    {
      title: 'Internal Note',
      dataIndex: 'internal_note',
      key: 'internal_note',
      width: 200,
    },
    {
      title: 'External Message',
      dataIndex: 'external_message',
      key: 'external_message',
      width: 200,
    },
    {
      title: 'Assignments Extended',
      dataIndex: 'assignments_extended',
      key: 'assignments_extended',
      width: 150,
    },
    {
      title: 'Fees Voided',
      dataIndex: 'fees_voided',
      key: 'fees_voided',
      width: 120,
      render: (value: number) => numeral(value).format(CURRENCY_FORMAT),
    },
    {
      title: 'Fees Refunded',
      dataIndex: 'fees_refunded',
      key: 'fees_refunded',
      width: 120,
      render: (value: number) => numeral(value).format(CURRENCY_FORMAT),
    },
  ];

  return (
    <div>
      {activeExtension && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 500 }}>
            Due dates extended until{' '}
            <span style={{ color: token.colorPrimary, fontWeight: 700 }}>
              {dayjs(activeExtension.end_date).format('MMMM D, YYYY')}
            </span>{' '}
            <span style={{ color: token.colorWarning, fontWeight: 700 }}>
              ({activeExtension.days} days)
            </span>{' '}
            {activeExtension.internal_note && (
              <>
                due to{' '}
                <span style={{ color: token.colorPrimary, fontWeight: 700 }}>
                  {activeExtension.internal_note}
                </span>
              </>
            )}
          </div>
          <div style={{ fontSize: 15, marginTop: 2 }}>
            by{' '}
            <span style={{ color: token.colorPrimary, fontWeight: 700 }}>
              {activeExtension.started_by_user?.full_name}
            </span>{' '}
            on{' '}
            <span style={{ color: token.colorPrimary, fontWeight: 700 }}>
              {dayjs(activeExtension.started_on).format('MMMM D, YYYY')}
            </span>
          </div>
        </div>
      )}
      {dbUser?.userPermissions.ExtendAssignments && (
        <>
          <Typography.Title
            level={3}
            style={{ marginTop: 0, marginBottom: '16px' }}
          >
            Extend due dates
          </Typography.Title>
          <Form
            form={form}
            name="extensions"
            layout="vertical"
            onFinish={handleEditSubmit}
            initialValues={{
              days: 1,
              internal_note: '',
              external_message: '',
            }}
            style={{ marginTop: -10, marginBottom: -10 }}
          >
            <Form.Item
              label="Extend due dates by:"
              style={{ marginBottom: 10, width: '500px' }}
              help={helperText}
            >
              <Form.Item name="days" noStyle>
                <InputNumber
                  placeholder="1"
                  min={1}
                  disabled={
                    isLoading || !dbUser?.userPermissions.ExtendAssignments
                  }
                />
              </Form.Item>

              <span className="ant-form-text" style={{ marginInlineStart: 8 }}>
                day(s)
              </span>
            </Form.Item>

            <Form.Item
              name="internal_note"
              label="Internal note"
              rules={[
                {
                  type: 'string',
                  required: true,
                  min: 1,
                  message: 'cannot be empty',
                },
                {
                  max: 150,
                  message: 'cannot exceed 150 characters',
                },
              ]}
              style={{ marginBottom: 10, width: '500px' }}
            >
              <TextArea
                placeholder="Only for internal use."
                disabled={
                  isLoading || !dbUser?.userPermissions.ExtendAssignments
                }
                rows={3}
                maxLength={150}
                showCount
              />
            </Form.Item>

            <Form.Item
              name="external_message"
              label="External message"
              rules={[
                {
                  type: 'string',
                  required: true,
                  min: 1,
                  message: 'cannot be empty',
                },
                {
                  max: 150,
                  message: 'cannot exceed 150 characters',
                },
              ]}
              style={{ marginBottom: 10, width: '500px' }}
            >
              <TextArea
                placeholder="This message will be sent to the customer."
                disabled={
                  isLoading || !dbUser?.userPermissions.ExtendAssignments
                }
                rows={3}
                maxLength={150}
                showCount
              />
            </Form.Item>
            <Popconfirm
              title="Create Extension"
              description={
                <p>
                  Are you sure you want to extend assignments by {days} days?
                </p>
              }
              onConfirm={() => form.submit()}
              okText="Yes"
              cancelText="No"
            >
              <Button
                loading={isLoading}
                type="primary"
                disabled={!dbUser?.userPermissions.ExtendAssignments}
              >
                Extend
              </Button>
            </Popconfirm>
          </Form>
        </>
      )}

      <div style={{ marginTop: '44px' }}>
        <Typography.Title
          level={3}
          style={{ marginTop: 0, marginBottom: '16px' }}
        >
          Extensions History
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: '16px' }}
        >
          This table takes 10 minutes to refresh.
        </Typography.Text>
        <Table
          rowKey="extension_id"
          columns={columns}
          dataSource={extensionsData?.listAssignmentExtension?.list || []}
          loading={extensionsLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showTotal: (total, range) =>
              `${range[0]} - ${range[1]} of ${total} extensions`,
          }}
        />
      </div>
    </div>
  );
};

export default ExtensionTab;
